/**
 * Tier & Location Funnel Report — /funnel/tiers
 *
 * Client-side breakdown of how page views convert into:
 *   1. #book clicks   (`book_click`)
 *   2. /inquire clicks (`*inquire*` CTAs)
 *   3. /inquire views  (`inquire_view`)
 *   4. Inquiry submits (`inquiry_submit`)
 *
 * Sliced by retainer tier and by CTA location (sticky bar, hero, pricing
 * matrix, etc.) so drop-off is attributable to a specific placement.
 *
 * Reads the same local event store as /funnel — no backend writes.
 */
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { SiteNav } from "@/components/site/SiteNav";
import { SiteFooter } from "@/components/site/SiteFooter";
import { PageMeta } from "@/components/site/PageMeta";
import { Button } from "@/components/ui/button";

type Evt = { event: string; ts: number; [k: string]: unknown };

const STORE_KEY = "drsully:funnel-events:v1";
const MAX_EVENTS = 2000;

const str = (v: unknown, fallback = "—") => {
  const s = typeof v === "string" || typeof v === "number" ? String(v) : "";
  return s.trim() ? s : fallback;
};

/** dataLayer entries use `ts` (events) or `cta_ts` (CTA clicks). */
function normalize(list: unknown[]): Evt[] {
  const seen = new Set<string>();
  const out: Evt[] = [];
  for (const raw of list) {
    if (!raw || typeof raw !== "object") continue;
    const e = raw as Record<string, unknown>;
    const ts = typeof e.ts === "number" ? e.ts : typeof e.cta_ts === "number" ? e.cta_ts : null;
    if (typeof e.event !== "string" || ts === null) continue;
    const key = `${e.event}|${ts}|${e.cta_name ?? ""}|${e.cta_path ?? e.path ?? ""}`;
    if (seen.has(key)) continue;
    seen.add(key);
    out.push({ ...(e as Evt), event: e.event, ts });
  }
  return out.slice(-MAX_EVENTS);
}

function loadEvents(): Evt[] {
  if (typeof window === "undefined") return [];
  let stored: unknown[] = [];
  try {
    const raw = window.localStorage.getItem(STORE_KEY);
    stored = raw ? (JSON.parse(raw) as unknown[]) : [];
  } catch {
    stored = [];
  }
  return normalize([...stored, ...((window.dataLayer ?? []) as unknown[])]);
}

const ctaName = (e: Evt) => str(e.cta_name, "");

const isPageView = (e: Evt) => e.event === "page_view";
const isBookClick = (e: Evt) => e.event === "book_click" || ctaName(e) === "book_click";
const isInquireClick = (e: Evt) =>
  /inquire/i.test(ctaName(e)) &&
  !/view|confirmation/i.test(ctaName(e));
const isInquireView = (e: Evt) => ctaName(e) === "inquire_view";
const isInquirySubmit = (e: Evt) =>
  e.event === "inquiry_submit" || ctaName(e) === "inquiry_submit";

/** Tier label for grouping — several event families spell it differently. */
const tierOf = (e: Evt) =>
  str(e.tier ?? e.tier_slug ?? e.budget_range, "unattributed")
    .toLowerCase()
    .replace(/[\s_]+/g, "-");

/** Placement label for grouping. */
const locationOf = (e: Evt) => {
  if (e.is_sticky === true) return "sticky_mobile";
  return str(
    e.location ?? e.surface ?? e.source ?? e.tier_source ?? e.cta_path ?? e.path,
    "unknown",
  );
};

type Row = {
  key: string;
  views: number;
  book: number;
  inquireClick: number;
  inquireView: number;
  submit: number;
};

function tally(events: Evt[], keyOf: (e: Evt) => string, includeViews: boolean): Row[] {
  const map = new Map<string, Row>();
  const row = (k: string) => {
    let r = map.get(k);
    if (!r) {
      r = { key: k, views: 0, book: 0, inquireClick: 0, inquireView: 0, submit: 0 };
      map.set(k, r);
    }
    return r;
  };
  for (const e of events) {
    if (isPageView(e)) {
      if (includeViews) row(keyOf(e)).views += 1;
      continue;
    }
    if (isBookClick(e)) row(keyOf(e)).book += 1;
    else if (isInquireClick(e)) row(keyOf(e)).inquireClick += 1;
    else if (isInquireView(e)) row(keyOf(e)).inquireView += 1;
    else if (isInquirySubmit(e)) row(keyOf(e)).submit += 1;
  }
  return [...map.values()].sort(
    (a, b) => b.book + b.inquireClick + b.submit - (a.book + a.inquireClick + a.submit),
  );
}

const pct = (num: number, den: number) => (den > 0 ? `${Math.round((num / den) * 100)}%` : "—");

function BreakdownTable({
  title,
  caption,
  rows,
  label,
  baseline,
}: {
  title: string;
  caption: string;
  rows: Row[];
  label: string;
  baseline: number;
}) {
  return (
    <div className="border border-border bg-navy/70 p-6">
      <h2 className="font-display uppercase text-white text-base tracking-wide">{title}</h2>
      <p className="text-silver/80 text-xs mt-2 leading-relaxed">{caption}</p>

      {rows.length === 0 ? (
        <p className="text-silver text-sm mt-5">No events captured for this breakdown yet.</p>
      ) : (
        <div className="overflow-x-auto mt-5">
          <table className="w-full text-sm min-w-[560px]">
            <thead className="text-[10px] uppercase tracking-[0.2em] text-silver/70">
              <tr>
                <th className="text-left py-2">{label}</th>
                <th className="text-right py-2">#book</th>
                <th className="text-right py-2">/inquire clicks</th>
                <th className="text-right py-2">/inquire views</th>
                <th className="text-right py-2">Submits</th>
                <th className="text-right py-2">Click→Submit</th>
                <th className="text-right py-2">Share of total</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const clicks = r.book + r.inquireClick;
                return (
                  <tr key={r.key} className="border-t border-border">
                    <td className="py-2 text-white">{r.key}</td>
                    <td className="py-2 text-right text-silver">{r.book}</td>
                    <td className="py-2 text-right text-silver">{r.inquireClick}</td>
                    <td className="py-2 text-right text-silver">{r.inquireView}</td>
                    <td className="py-2 text-right text-gold font-display">{r.submit}</td>
                    <td className="py-2 text-right text-silver">{pct(r.submit, clicks)}</td>
                    <td className="py-2 text-right text-silver">{pct(clicks, baseline)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

const FunnelTiers = () => {
  const [events, setEvents] = useState<Evt[]>([]);

  useEffect(() => {
    setEvents(loadEvents());
    const refresh = () => setEvents(loadEvents());
    window.addEventListener("cta:tracked", refresh as EventListener);
    window.addEventListener("conversion:tracked", refresh as EventListener);
    const timer = window.setInterval(refresh, 4000);
    return () => {
      window.removeEventListener("cta:tracked", refresh as EventListener);
      window.removeEventListener("conversion:tracked", refresh as EventListener);
      window.clearInterval(timer);
    };
  }, []);

  const byTier = useMemo(() => tally(events, tierOf, false), [events]);
  const byLocation = useMemo(() => tally(events, locationOf, false), [events]);

  const totals = useMemo(() => {
    const views = events.filter(isPageView).length;
    const book = events.filter(isBookClick).length;
    const inquireClick = events.filter(isInquireClick).length;
    const inquireView = events.filter(isInquireView).length;
    const submit = events.filter(isInquirySubmit).length;
    return { views, book, inquireClick, inquireView, submit };
  }, [events]);

  const totalClicks = totals.book + totals.inquireClick;

  const summary = [
    { label: "Page views", value: totals.views, rate: "—" },
    { label: "#book clicks", value: totals.book, rate: pct(totals.book, totals.views) },
    {
      label: "/inquire clicks",
      value: totals.inquireClick,
      rate: pct(totals.inquireClick, totals.views),
    },
    {
      label: "/inquire views",
      value: totals.inquireView,
      rate: pct(totals.inquireView, totalClicks),
    },
    { label: "Inquiry submits", value: totals.submit, rate: pct(totals.submit, totals.inquireView) },
  ];

  return (
    <div className="min-h-screen bg-background text-foreground">
      <PageMeta
        title="Tier & Placement Funnel Report — DrSullySpeaks"
        description="Internal report of /inquire and #book conversion rates by retainer tier and CTA location."
        path="/funnel/tiers"
        noindex
      />
      <SiteNav />

      <section className="pt-32 pb-10 md:pt-40">
        <div className="container max-w-5xl">
          <span className="eyebrow">Internal · Client-side only</span>
          <h1 className="font-display font-bold uppercase text-white text-3xl md:text-5xl mt-5 leading-[1.05]">
            Tier &amp; Placement <span className="text-gradient-gold">Funnel Report</span>
          </h1>
          <p className="text-silver text-base mt-6 max-w-2xl leading-relaxed">
            Conversion into <span className="text-white">/inquire</span> and{" "}
            <span className="text-white">#book</span>, broken down by retainer tier and by the CTA
            placement that produced the click. Reads events captured in this browser.
          </p>
        </div>
      </section>

      <section className="pb-10">
        <div className="container max-w-5xl grid grid-cols-2 md:grid-cols-5 gap-3">
          {summary.map((s) => (
            <div key={s.label} className="border border-border bg-navy/70 p-4">
              <div className="text-[10px] uppercase tracking-[0.2em] text-silver/70">{s.label}</div>
              <div className="font-display text-white text-2xl mt-2">{s.value}</div>
              <div className="text-gold text-[11px] uppercase tracking-[0.18em] mt-1">
                {s.rate} step rate
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="pb-10">
        <div className="container max-w-5xl space-y-6">
          <BreakdownTable
            title="By retainer tier"
            caption="Tier attribution comes from the last tier the visitor selected before clicking, or the tier attached to the inquiry."
            rows={byTier}
            label="Tier"
            baseline={totalClicks}
          />
          <BreakdownTable
            title="By CTA location"
            caption="Placement is the nearest labelled section, sticky mobile bar, or form surface that fired the event."
            rows={byLocation}
            label="Location"
            baseline={totalClicks}
          />
        </div>
      </section>

      <section className="pb-24">
        <div className="container max-w-5xl flex flex-wrap gap-3">
          <Button variant="outline" onClick={() => setEvents(loadEvents())}>
            Refresh
          </Button>
          <Link
            to="/funnel"
            className="inline-flex items-center px-4 py-2 border border-gold/40 text-white text-xs uppercase tracking-[0.2em] hover:bg-gold/10"
          >
            ← Overall funnel
          </Link>
        </div>
      </section>

      <SiteFooter />
    </div>
  );
};

export default FunnelTiers;
